import React, { useState } from "react";
import {
  DemoResponsiveGrid,
  DemoWorkbenchButton,
  DemoWorkbenchCard,
  DemoWorkbenchControlBar,
  DemoWorkbenchCopy,
  DemoWorkbenchInset,
  DemoWorkbenchStack,
} from "@csk/demo-ui";
import { ATTACK_SCENARIO_STEPS } from "./scenario-walkthrough-panel/attackScenarioSteps.js";

const GATE_LABELS = {
  first: "Gate 1: Parse",
  focus: "Gate 3: Bridge Matrix",
  last: "Gate 8: Receipt",
};

const ScenarioWalkthroughPanel = (): React.JSX.Element => {
  const [step, setStep] = useState(0);
  const current = ATTACK_SCENARIO_STEPS[step] ?? ATTACK_SCENARIO_STEPS[0];
  const currentGate = current.gate;

  return (
    <DemoWorkbenchStack>
      <DemoWorkbenchCard title="Attack Scenario Walkthrough">
        <DemoWorkbenchCopy>
          Step through a prompt injection attack against baseline vs governed
          executor.
        </DemoWorkbenchCopy>
      </DemoWorkbenchCard>

      <DemoWorkbenchControlBar ariaLabel="Scenario steps">
        {ATTACK_SCENARIO_STEPS.map((scenarioStep, index) => (
          <DemoWorkbenchButton
            key={scenarioStep.title}
            tone={
              index === step ? "accent" : index < step ? "success" : "default"
            }
            active={index <= step}
            ariaLabel={`Show scenario step ${index + 1}`}
            onClick={() => {
              setStep(index);
            }}
          >
            {index + 1}
          </DemoWorkbenchButton>
        ))}
      </DemoWorkbenchControlBar>

      <DemoWorkbenchCard
        compact
        title={current.title}
        style={{
          borderColor: current.critical
            ? "rgba(239,68,68,0.42)"
            : "rgba(71,85,105,0.58)",
          background: current.critical
            ? "rgba(127,29,29,0.16)"
            : "rgba(15,23,42,0.78)",
        }}
      >
        <DemoResponsiveGrid minColumnWidth={260} gap={16}>
          <ScenarioLane
            title="BASELINE (no governance)"
            tone="danger"
            body={current.baseline}
            result={current.baselineResult}
            fallback="Already exfiltrated at step 1."
          />
          <ScenarioLane
            title="GOVERNED (8-gate pipeline)"
            tone={current.critical ? "success" : "default"}
            body={current.governed}
            result={current.governedResult}
          />
        </DemoResponsiveGrid>
      </DemoWorkbenchCard>

      <div className="flex gap-1">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((gate) => (
          <div
            key={gate}
            className="h-2 flex-1 rounded"
            style={{
              background:
                gate < currentGate
                  ? "rgba(34,197,94,0.42)"
                  : gate === currentGate
                    ? current.critical
                      ? "rgba(239,68,68,0.72)"
                      : "rgba(20,184,166,0.72)"
                    : "rgba(30,41,59,0.72)",
            }}
          />
        ))}
      </div>
      <div className="flex justify-between text-xs text-gray-600">
        <span>{GATE_LABELS.first}</span>
        <span>{GATE_LABELS.focus}</span>
        <span>{GATE_LABELS.last}</span>
      </div>

      <DemoWorkbenchControlBar
        ariaLabel="Scenario navigation"
        style={{ justifyContent: "space-between" }}
      >
        <DemoWorkbenchButton
          disabled={step === 0}
          ariaLabel="Show previous scenario step"
          onClick={() => {
            setStep(Math.max(0, step - 1));
          }}
        >
          Previous
        </DemoWorkbenchButton>
        <DemoWorkbenchButton
          tone="accent"
          disabled={step === ATTACK_SCENARIO_STEPS.length - 1}
          ariaLabel="Show next scenario step"
          onClick={() => {
            setStep(Math.min(ATTACK_SCENARIO_STEPS.length - 1, step + 1));
          }}
        >
          Next
        </DemoWorkbenchButton>
      </DemoWorkbenchControlBar>
    </DemoWorkbenchStack>
  );
};

interface ScenarioLaneProps {
  readonly title: string;
  readonly tone: "default" | "danger" | "success";
  readonly body: string | null;
  readonly result: string | null;
  readonly fallback?: string;
}

function ScenarioLane({
  title,
  tone,
  body,
  result,
  fallback = "No event in this lane.",
}: ScenarioLaneProps): React.JSX.Element {
  const accent =
    tone === "danger" ? "#f87171" : tone === "success" ? "#34d399" : "#cbd5e1";
  return (
    <DemoWorkbenchInset
      compact
      title={title}
      style={{ borderColor: `${accent}55` }}
    >
      {body !== null ? (
        <p className="m-0 rounded bg-gray-900 p-2 text-xs text-gray-400">
          {body}
        </p>
      ) : null}
      {result !== null ? (
        <p
          className="mt-2 rounded p-2 font-mono text-xs"
          style={{ background: `${accent}1f`, color: accent }}
        >
          {result}
        </p>
      ) : null}
      {body === null && result === null ? (
        <p className="m-0 text-xs italic text-gray-600">{fallback}</p>
      ) : null}
    </DemoWorkbenchInset>
  );
}

export default ScenarioWalkthroughPanel;
